import { z } from 'zod/v4';
import { UserDetailResponseSchema } from './get-by-id.js';

/**
 * Admin user impersonating someone — the legacy `unImpersonatedUser` is a
 * spread of the admin's own (unscoped) user row plus `company`/
 * `impersonateId` (see `authenticate-from-token.js`). `looseObject` because
 * only a couple of fields are read anywhere downstream; the rest passes
 * through unvalidated like the rest of this response.
 */
const UnImpersonatedUserSchema = z.looseObject({
  id: z.string().optional(),
  email: z.string().optional(),
  /** Whether the operator behind an impersonated session (the "real" actor)
   *  holds any IAM role in the retraced tenant — computed once, before
   *  impersonation swaps the identity (`isRetracedStaffActor` against the
   *  retraced tenant). */
  isRetracedStaff: z.boolean().optional(),
});

/**
 * Response for `GET /users/me` — identical to `UserDetailResponseSchema`
 * (both are backed by the same `UserDetailService.getById`) plus
 * `unImpersonatedUser`, which the legacy `/v3/me` route merges in from
 * `ctx.unImpersonatedUser` and `GET /users/:id` does not expose.
 */
export const MeResponseSchema = UserDetailResponseSchema.extend({
  unImpersonatedUser: UnImpersonatedUserSchema.nullable(),
  /** IAM-native replacement for the frontend's `role` prefix checks
   *  (`isRetracedStaff`/`isRetracedAdmin` in `managementAccess.ts` /
   *  `postLoginPath.ts`) — computed server-side from actual IAM membership
   *  rather than string-matching a role value that can be company-scoped. */
  isRetracedStaff: z.boolean(),
  isRetracedAdmin: z.boolean(),
  /** IAM-native replacement for the frontend's `role`-disagreement check
   *  (`isScopedToCustomerCompany` in `managementAccess.ts`) — true iff the
   *  *current* session company is the retraced tenant itself. */
  isActingInOwnTenant: z.boolean(),
});

export type MeResponse = z.infer<typeof MeResponseSchema>;
